import { useCallback, useEffect, useState } from "react";
import { FileText, History, Loader2, Lock, Plus, Save, Trash2, Wrench } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle } from "@/components/ui/dialog";
import { Chip, Loading, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";
import { dateTime } from "@/lib/format";

export const PermissionsManager = () => {
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [draft, setDraft] = useState(null);
  const [adding, setAdding] = useState(null);
  const [history, setHistory] = useState([]);
  const [roleHistory, setRoleHistory] = useState([]);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const res = await api.get("/admin/permissions");
    setData(res.data);
    try {
      const log = await api.get("/admin/permission-history?limit=40");
      setHistory(log.data.items);
    } catch { setHistory([]); }
  }, []);

  useEffect(() => { load().catch(() => setData(false)); }, [load]);

  const openRole = async (key) => {
    setBusy(`open-${key}`);
    try {
      const res = await api.get(`/admin/permissions/${key}`);
      setDetail(res.data);
      try {
        const log = await api.get(`/admin/permission-history?role=${key}`);
        setRoleHistory(log.data.items);
      } catch { setRoleHistory([]); }
      setDraft({
        pages: Object.fromEntries(res.data.pages.map((p) => [p.resource,
          { read: p.read, write: p.write }])),
        functions: Object.fromEntries(res.data.functions.map((f) =>
          [`${f.resource}:${f.action}`, f.granted])),
      });
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const saveRole = async () => {
    setBusy("save");
    try {
      const res = await api.put(`/admin/permissions/${detail.role.key}`, draft);
      setDetail(res.data);
      toast.success(`${detail.role.label} updated — it applies to every request from now on`);
      await load();
      try {
        const log = await api.get(`/admin/permission-history?role=${detail.role.key}`);
        setRoleHistory(log.data.items);
      } catch { /* history is a nicety, not the save */ }
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const addRole = async () => {
    setBusy("add");
    try {
      const res = await api.post("/admin/roles", { label: adding });
      toast.success(`${res.data.role.label} added — nothing is granted to it yet`);
      setAdding(null);
      await load();
      await openRole(res.data.role.key);
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const removeRole = async (key, label) => {
    setBusy(`del-${key}`);
    try {
      await api.delete(`/admin/roles/${key}`);
      toast.success(`${label} removed`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  if (!data) return <Loading label="Loading the permissions" />;

  return (
    <div className="space-y-4">
      <Panel testId="panel-permissions" kicker="FR-AUTH-002"
             title="Roles and what each one may reach"
             actions={data.editable && (
               <Button size="sm" data-testid="add-role-button"
                       onClick={() => setAdding("")}
                       className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                 <Plus className="mr-1.5 h-3.5 w-3.5" /> Add a role
               </Button>
             )}>
        <p className="px-4 pt-3 text-[12px] leading-relaxed text-muted-foreground">
          {data.total_pages} screens and {data.total_functions} special powers are guarded on the
          server. Open a role to say which screens it may read and write, and which powers it holds.
          {data.customised.length > 0
            && ` ${data.customised.length} area(s) are customised for this charity.`}
        </p>
        <Table testId="permissions-table"
               head={<><Th>Role</Th><Th>People</Th><Th>Screens they can read</Th>
                 <Th>Screens they can change</Th><Th>Special powers</Th><Th /></>}>
          {data.roles.map((role) => (
            <tr key={role.key} className="row-hover cursor-pointer"
                data-testid={`permissions-role-${role.key}`}
                onClick={() => openRole(role.key)}>
              <Td>
                <div className="flex items-center gap-2">
                  <span className="text-[12.5px] font-medium text-foreground">{role.label}</span>
                  {role.custom && (
                    <Chip className="border-primary/40 bg-primary/10 text-primary">ours</Chip>
                  )}
                  {role.privileged && (
                    <Chip className="border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300">
                      privileged
                    </Chip>
                  )}
                </div>
                <div className="font-mono text-[10px] text-muted-foreground/70">{role.key}</div>
              </Td>
              <Td className="font-mono text-[11px]"
                  data-testid={`role-people-${role.key}`}>{role.people}</Td>
              <Td className="font-mono text-[11px]"
                  data-testid={`role-read-${role.key}`}>
                {role.pages_read} of {data.total_pages}
              </Td>
              <Td className="font-mono text-[11px]"
                  data-testid={`role-write-${role.key}`}>
                {role.pages_write} of {data.total_pages}
              </Td>
              <Td className="font-mono text-[11px]"
                  data-testid={`role-functions-${role.key}`}>
                {role.functions} of {data.total_functions}
              </Td>
              <Td>
                <div className="flex justify-end gap-1.5">
                  <Button size="sm" variant="outline" className="h-7 rounded-full text-[10.5px]"
                          data-testid={`open-role-${role.key}`}
                          onClick={(e) => { e.stopPropagation(); openRole(role.key); }}>
                    {busy === `open-${role.key}`
                      ? <Loader2 className="h-3 w-3 animate-spin" /> : "Permissions"}
                  </Button>
                  {role.custom && data.editable && (
                    <Button size="sm" variant="ghost" className="h-7 rounded-full px-2"
                            data-testid={`delete-role-${role.key}`}
                            onClick={(e) => { e.stopPropagation(); removeRole(role.key, role.label); }}>
                      {busy === `del-${role.key}`
                        ? <Loader2 className="h-3 w-3 animate-spin" />
                        : <Trash2 className="h-3 w-3 text-muted-foreground" />}
                    </Button>
                  )}
                </div>
              </Td>
            </tr>
          ))}
        </Table>
        <div className="border-t border-border px-4 py-2.5 text-[11px] text-muted-foreground"
             data-testid="permissions-audit-line">
          {data.updated_by
            ? `Last changed by ${data.updated_by} · ${dateTime(data.updated_at)}`
            : "Still on the platform default permissions."}
          {" "}A padlock marks a permission that cannot be taken away, so nobody can lock the
          charity out of user administration.
        </div>
      </Panel>

      <Panel testId="panel-permission-history" kicker="For the auditor's file"
             title="Who changed what, and when">
        <ChangeLog items={history} testId="permission-history-list" showRole
                   empty="No permission has been changed since this charity went live." />
      </Panel>

      <Dialog open={Boolean(detail)} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl" data-testid="role-permissions-dialog">
          <DialogHeader>
            <DialogTitle>{detail?.role.label}</DialogTitle>
            <DialogDescription>
              What this role may reach. Read lets them see a screen; change lets them alter it.
              {!detail?.editable && " Read-only — only a tenant admin may change permissions."}
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="pages">
            <TabsList>
              <TabsTrigger value="pages" data-testid="tab-role-pages">
                <FileText className="mr-1.5 h-3.5 w-3.5" /> Pages
              </TabsTrigger>
              <TabsTrigger value="functions" data-testid="tab-role-functions">
                <Wrench className="mr-1.5 h-3.5 w-3.5" /> Function access
              </TabsTrigger>
              <TabsTrigger value="history" data-testid="tab-role-history">
                <History className="mr-1.5 h-3.5 w-3.5" /> History
              </TabsTrigger>
            </TabsList>

            <TabsContent value="pages" className="mt-3">
              <div className="max-h-[52vh] overflow-y-auto rounded-xl border border-border">
                <table className="w-full border-collapse">
                  <thead className="sticky top-0 bg-card">
                    <tr className="border-b border-border">
                      <Th>Screen</Th><Th className="w-24">Read</Th><Th className="w-24">Change</Th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {(detail?.pages || []).map((page) => (
                      <tr key={page.resource} data-testid={`page-row-${page.resource}`}>
                        <Td>
                          <span className="text-[12.5px] text-foreground">{page.label}</span>
                          {page.read_everyone && (
                            <span className="ml-2 text-[10px] text-muted-foreground">
                              readable by everyone by default
                            </span>
                          )}
                        </Td>
                        <Td>
                          <Checkbox data-testid={`page-read-${page.resource}`}
                                    checked={Boolean(draft?.pages[page.resource]?.read)}
                                    disabled={!detail.editable || page.read_locked}
                                    onCheckedChange={(v) => setDraft((d) => ({ ...d,
                                      pages: { ...d.pages,
                                        [page.resource]: { ...d.pages[page.resource],
                                          read: Boolean(v) } } }))} />
                          {page.read_locked && (
                            <Lock className="ml-1 inline h-2.5 w-2.5 text-muted-foreground/60" />
                          )}
                        </Td>
                        <Td>
                          {page.has_write ? (
                            <>
                              <Checkbox data-testid={`page-write-${page.resource}`}
                                        checked={Boolean(draft?.pages[page.resource]?.write)}
                                        disabled={!detail.editable || page.write_locked}
                                        onCheckedChange={(v) => setDraft((d) => ({ ...d,
                                          pages: { ...d.pages,
                                            [page.resource]: { ...d.pages[page.resource],
                                              write: Boolean(v) } } }))} />
                              {page.write_locked && (
                                <Lock className="ml-1 inline h-2.5 w-2.5 text-muted-foreground/60" />
                              )}
                            </>
                          ) : (
                            <span className="text-[10.5px] text-muted-foreground">—</span>
                          )}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            <TabsContent value="functions" className="mt-3">
              <div className="max-h-[52vh] space-y-1.5 overflow-y-auto pr-1"
                   data-testid="role-functions-list">
                {(detail?.functions || []).map((fn) => {
                  const key = `${fn.resource}:${fn.action}`;
                  return (
                    <label key={key}
                           className="flex items-center gap-3 rounded-xl border border-border px-3 py-2">
                      <Checkbox data-testid={`function-${fn.resource}-${fn.action}`}
                                checked={Boolean(draft?.functions[key])}
                                disabled={!detail.editable || fn.locked}
                                onCheckedChange={(v) => setDraft((d) => ({ ...d,
                                  functions: { ...d.functions, [key]: Boolean(v) } }))} />
                      <span className="flex-1 text-[12.5px] text-foreground">
                        {fn.label}
                        <span className="ml-2 text-[11px] text-muted-foreground">{fn.page}</span>
                      </span>
                      {fn.locked && <Lock className="h-3 w-3 text-muted-foreground/60" />}
                    </label>
                  );
                })}
              </div>
            </TabsContent>

            <TabsContent value="history" className="mt-3">
              <ChangeLog items={roleHistory} testId="role-history-list"
                         empty="Nothing has been changed on this role yet." />
            </TabsContent>
          </Tabs>

          {detail?.editable && (
            <DialogFooter>
              <Button onClick={saveRole} disabled={busy === "save"}
                      data-testid="role-permissions-save"
                      className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                {busy === "save" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  : <Save className="mr-1.5 h-3.5 w-3.5" />} Save these permissions
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={adding !== null} onOpenChange={(o) => !o && setAdding(null)}>
        <DialogContent className="max-w-md" data-testid="add-role-dialog">
          <DialogHeader>
            <DialogTitle>Add a role</DialogTitle>
            <DialogDescription>
              It starts with nothing granted — open it afterwards and give it what it needs.
            </DialogDescription>
          </DialogHeader>
          <Input data-testid="new-role-label" value={adding || ""}
                 placeholder="Kitchen supervisor"
                 onChange={(e) => setAdding(e.target.value)} />
          <DialogFooter>
            <Button onClick={addRole} disabled={busy === "add" || (adding || "").trim().length < 3}
                    data-testid="new-role-save"
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
              {busy === "add" && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              Add the role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const KIND = {
  created: { label: "role added", tone: "border-primary/40 bg-primary/10 text-primary" },
  removed: { label: "role removed",
    tone: "border-red-500/30 bg-red-500/10 text-red-600 dark:text-red-300" },
  changed: { label: "access changed", tone: "border-border bg-muted text-muted-foreground" },
};

const ChangeLog = ({ items, testId, empty, showRole = false }) => (
  <div className="max-h-[52vh] space-y-2 overflow-y-auto px-4 py-3" data-testid={testId}>
    {items.length === 0 && (
      <p className="text-[12.5px] text-muted-foreground" data-testid={`${testId}-empty`}>{empty}</p>
    )}
    {items.map((entry) => (
      <div key={entry.change_ref} data-testid={`change-${entry.change_ref}`}
           className="rounded-xl border border-border px-3.5 py-2.5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-[12.5px] text-foreground">
            {showRole && <span className="font-medium">{entry.role_label} · </span>}
            {entry.by}
          </p>
          <div className="flex items-center gap-2">
            <Chip className={(KIND[entry.kind] || KIND.changed).tone}>
              {(KIND[entry.kind] || KIND.changed).label}
            </Chip>
            <span className="font-mono text-[10.5px] text-muted-foreground">
              {dateTime(entry.at)}
            </span>
          </div>
        </div>
        {entry.added.length > 0 && (
          <p className="mt-1.5 text-[11.5px] leading-relaxed text-emerald-600 dark:text-emerald-400"
             data-testid={`change-added-${entry.change_ref}`}>
            gained: {entry.added.map((a) => a.label).join(" · ")}
          </p>
        )}
        {entry.removed.length > 0 && (
          <p className="mt-1 text-[11.5px] leading-relaxed text-amber-600 dark:text-amber-400"
             data-testid={`change-removed-${entry.change_ref}`}>
            lost: {entry.removed.map((a) => a.label).join(" · ")}
          </p>
        )}
        {entry.added.length === 0 && entry.removed.length === 0 && (
          <p className="mt-1 text-[11.5px] text-muted-foreground">
            {entry.kind === "created" ? "Added with nothing granted."
              : "Removed with nothing left granted."}
          </p>
        )}
        <p className="mt-1 font-mono text-[10px] text-muted-foreground/70">
          {entry.change_ref} · {entry.by_email}
        </p>
      </div>
    ))}
  </div>
);
